import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithImmer } from 'jotai-immer';
import { dbui_v1_Query_LookupResult } from '@antimatterhq/asl-api-ts-v1';

// Type definitions
export type QueryResultRow = {
  id: string; // Stable ID generated from buildStableId
  [key: string]: string;
};

const FIRST_ITEM_INDEX = 100_000;

type QueryTablePageStatus = 'loaded' | 'loading' | 'error' | 'evicted';

interface QueryTablePage {
  rows: QueryResultRow[] | undefined; // if evicted, this will be undefined
  status: QueryTablePageStatus;
  body: dbui_v1_Query_LookupResult;
}

interface QueryTableMetadata {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoadingInitialPage: boolean;
  isLoadingNextPage: boolean;
  isLoadingPreviousPage: boolean;
  isError: boolean;
  error: Error | null;
  nextPageParams: { fields: Record<string, string> } | null;
  previousPageParams: { fields: Record<string, string> } | null;
  firstItemIndex: number;
}

export type QueryTableState = {
  rows: QueryResultRow[];
  pages: Record<string, QueryTablePage>;
  metadata: QueryTableMetadata;
};

// Helper function to create initial state
const createInitialState = (): QueryTableState => ({
  rows: [],
  pages: {},
  metadata: {
    hasNextPage: false,
    hasPreviousPage: false,
    isLoadingInitialPage: false,
    isLoadingNextPage: false,
    isLoadingPreviousPage: false,
    isError: false,
    error: null,
    nextPageParams: null,
    previousPageParams: null,
    firstItemIndex: FIRST_ITEM_INDEX,
  },
});

// Non-persisted atom family for storing query results per tab using Immer
export const queryTableStateAtomFamily = atomFamily(() =>
  atomWithImmer<QueryTableState>(createInitialState()),
);

// The atomWithImmer already provides set functionality with draft updates
// So we can remove the separate updateQueryTableStateAtomFamily

// Clear results when query changes or tab is closed
export const clearQueryTableStateAtomFamily = atomFamily((tabId: string) =>
  atom(null, (_get, set) => {
    set(queryTableStateAtomFamily(tabId), createInitialState());
  }),
);
